import { timeSheetsStore } from '@/app/features/time-sheets/time-sheets.store';
import { expensesStore } from '@/app/features/expenses/expenses.store';
import { notificationsStore } from '@/app/features/notifications/notifications.store';
import { preferencesStore } from '@/app/features/preferences/preferences.store';
import { assignmentsStore } from '@/app/features/assignments/assignments.store';
import { permPlacementsStore } from '@/app/features/perm-placements/perm-placements.store';
import { contractorsStore } from './features/contractors/contractors.store'; //'@/app/features/contractors/contractors.store';
import { clientsStore } from '@/app/features/clients/clients.store';
import { tesEmployeesStore } from '@/app/features/tes-employees/tes-employees.store';
import { messagesStore } from '@/app/features/tol-messages/tol-messages.store';
import { AppMutations, AppActions, AppGetters } from '@/app/app.keys';
import { errorsStore } from '@/app/features/tol-errors/tol-errors.store';
import { appService } from '@/app/app.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { RecentItemsActions } from '@/app/features/recent-items/recent-items.keys';
import { clientContactsStore } from './features/client-contacts/client-contacts.store';
import { suppliersStore } from './features/suppliers/suppliers.store';
import { supplierContactsStore } from './features/supplier-contacts/supplier-contacts.store';
import { recentItemsStore } from './features/recent-items/recent-items.store';
import { usersStore } from './features/users/users.store';
import { purchaseOrdersStore } from './features/purchase-orders/purchase-orders.store';
import { placementActivityStore } from './features/placement-activity/placement-activity.store';
import { advancedSearchStore } from './features/advanced-search/advanced-search.store';
import { profilesStore } from '@/app/features/profile/profile.store';
import { depositSlipsStore } from '@/app/features/deposit-slips/deposit-slips.store';
import { payrollOverviewsStore } from '@/app/features/payroll-overviews/payroll-overviews.store';
import { projectsStore } from '@/app/features/projects/projects.store';
import { batchUpdateListingStore } from '@/app/features/batch-update/batch-update-listing-store';
import { reportsStore } from '@/app/features/report/reports.store';
import { adminStore } from '@/app/features/admin/admin.store';
import { userActivityStore } from '@/app/features/user-activity/user-activity.store';
import { aodaStore } from '@/app/features/aoda/aoda.store';

export const getInitialState = () => {
  return {
    pageTitle: '',
    currentUser: null,
    unreadNotificationCount: null,
    error: null,
    locale: 'en',
    redirectTo: '',
    lastUserActiveTime: 0,
    refreshTokenModalIsVisible: false,
    isLoading: true,
    isRecentUser: 0,
    firstTimeApplicationAccess: false,
    accountVerification: {
      required: false,
      username: '',
      userFirstname: ''
    },
    globalValues: {
      rateTypes: [],
      branches: [],
      provinces: [],
      rebates: [],
      entityTypes: [],
      phoneTypes: [],
      contractorSources: [],
      divisions: [],
      departments: [],
      assignmentEndReasons: [],
      ibmLineOfBusiness: [],
      ibmSkillLevel: [],
      reportsList: []
    },
    timeOutReference: null,
    sessionRefreshTime: null,
    serviceUnavailable: false
  };
};

export const appStore = {
  modules: {
    errorsState: errorsStore,
    timeSheetsState: timeSheetsStore,
    expensesState: expensesStore,
    notificationsState: notificationsStore,
    preferencesState: preferencesStore,
    assignmentsState: assignmentsStore,
    permPlacementsState: permPlacementsStore,
    purchaseOrdersState: purchaseOrdersStore,
    contractorsState: contractorsStore,
    clientsState: clientsStore,
    clientContactsState: clientContactsStore,
    tesEmployeeState: tesEmployeesStore,
    messagesState: messagesStore,
    supplierState: suppliersStore,
    supplierContactsState: supplierContactsStore,
    recentItemsState: recentItemsStore,
    userStore: usersStore,
    advancedSearchState: advancedSearchStore,
    profileStoreState: profilesStore,
    depositSlipsState: depositSlipsStore,
    payrollOverviewsState: payrollOverviewsStore,
    projectsState: projectsStore,
    batchUpdateListingState: batchUpdateListingStore,
    reportsState: reportsStore,
    placementActivityState: placementActivityStore,
    adminState: adminStore,
    userActivityState: userActivityStore,
    aodaState: aodaStore
  },
  state: getInitialState(),
  getters: {
    [AppGetters.FirstTimeApplicationAccess]: state => {
      return state.firstTimeApplicationAccess;
    },
    [AppGetters.AccountVerificationRequired]: state => {
      return state.accountVerification.required;
    },
    [AppGetters.AccountVerificationUser]: state => {
      return state.accountVerification.username;
    },
    [AppGetters.AccountVerificationUserFirstname]: state => {
      return state.accountVerification.userFirstname;
    },
    [AppGetters.AssignmentRateTypes]: state => {
      return state.globalValues.rateTypes;
    },
    [AppGetters.RefreshTokenModalIsVisible]: state => {
      return state.refreshTokenModalIsVisible;
    },
    [AppGetters.LastActiveTime]: state => {
      return state.lastUserActiveTime;
    },
    [AppGetters.BranchNames]: state => {
      return state.globalValues.branches.map(branch => branch.name);
    },
    [AppGetters.ProvinceAbbreviations]: state => {
      return state.globalValues.provinces.map(province => province.abbr);
    },
    [AppGetters.Provinces]: state => {
      return state.globalValues.provinces;
    },
    [AppGetters.ProvinceNames]: state => {
      return state.globalValues.provinces.map(province => province.name);
    },
    [AppGetters.ClientRebates]: state => {
      return state.globalValues.rebates;
    },
    [AppGetters.EntityTypes]: state => {
      return state.globalValues.entityTypes.map(entityType => entityType.name);
    },
    [AppGetters.PhoneTypes]: state => {
      return state.globalValues.phoneTypes.map(phoneType => phoneType.name);
    },
    [AppGetters.ContractorSources]: state => {
      return state.globalValues.contractorSources.map(contractorSource => contractorSource.name);
    },
    [AppGetters.DivisionAbbrs]: state => {
      return state.globalValues.divisions;
    },
    [AppGetters.Departments]: state => {
      return state.globalValues.departments;
    },
    [AppGetters.AssignmentEndReasons]: state => {
      return state.globalValues.assignmentEndReasons;
    },
    [AppGetters.IbmLineOfBusiness]: state => {
      return state.globalValues.ibmLineOfBusiness;
    },
    [AppGetters.IbmSkillLevel]: state => {
      return state.globalValues.ibmSkillLevel;
    },
    [AppGetters.Reports]: state => {
      return state.globalValues.reportsList.map(report => report.name);
    },
    [AppGetters.IsRecentUser]: state => state.isRecentUser
  },
  mutations: {
    [AppMutations.SetFirstTimeApplicationAccess]: (state, firstTime = true) => {
      state.firstTimeApplicationAccess = firstTime;
    },
    [AppMutations.SetPageTitle]: function(state, title) {
      state.pageTitle = title;
    },
    [AppMutations.UpdateRefreshTokenModalIsVisible]: (state, isVisible) => {
      if (
        (state.lastUserActiveTime !== 0 && Date.now() - state.lastUserActiveTime > 3300000) ||
        !isVisible
      ) {
        state.refreshTokenModalIsVisible = isVisible;
      }
    },
    [AppMutations.UpdateLastActiveTime]: state => {
      state.lastUserActiveTime = Date.now();
    },
    [AppMutations.ResetSessionInactiveTime]: state => {
      state.lastUserActiveTime = 0;
    },
    [AppMutations.SetLoading]: function(state, isLoading = true) {
      state.isLoading = isLoading;
    },
    [AppMutations.RetrieveSessionSuccess]: function(state, user) {
      state.currentUser = user;
      state.isLoading = false;
      state.redirectTo = '';
    },
    [AppMutations.DismissSession]: function(state) {
      state.currentUser = null;
      state.isLoading = false;
      clearTimeout(state.timeOutReference);
      //quick reset of the timer array you just cleared
      state.timeOutReferences = [];
    },
    [AppMutations.ToggleUserType]: function(state) {
      const perms = ['isContractor', 'isApprover', 'isTesEmployee'];
      for (let i = 0; i < perms.length; i++) {
        if (state.currentUser[perms[i]]) {
          state.currentUser[perms[i]] = false;
          state.currentUser[perms[(i + 1) % perms.length]] = true;
          break;
        }
      }
    },
    [AppMutations.RetrieveGlobalValuesSuccess]: (state, globalValues) => {
      state.globalValues = globalValues;
    },
    [AppMutations.RetrieveUnreadNotificationCountSuccess]: (state, unreadNotificationCount) => {
      state.unreadNotificationCount = unreadNotificationCount;
    },
    [AppMutations.SetRedirect]: (state, redirect = null) => {
      if (redirect) {
        state.redirectTo = redirect;
      }
    },
    [AppMutations.InsertTimeOutReferences]: (state, reference) => {
      if (state.timeOutReference) {
        clearTimeout(state.timeOutReference);
      }
      state.timeOutReference = reference;
    },
    [AppMutations.SetSessionRefreshTime]: (state, refreshTime) => {
      state.sessionRefreshTime = refreshTime;
    },
    [AppMutations.SetUserLocale]: (state, locale) => {
      state.locale = locale;
    },
    [AppMutations.UpdateIsRecentUser]: (state, payload) => {
      state.isRecentUser = payload;
    },
    [AppMutations.UpdateAccountVerificationRequired]: (state, payload) => {
      state.accountVerification = payload;
    },
    [AppMutations.SetServiceUnavailable]: (state, payload) => {
      state.serviceUnavailable = payload.status !== 200;
    }
    /*[AppMutations.ResetAllModulesStates]: state => {
     
    }*/
  },
  actions: {
    [AppActions.SetFirstTimeApplicationAccess]: ({ commit }, firstTime = true) => {
      commit(AppMutations.SetFirstTimeApplicationAccess, firstTime);
    },
    [AppActions.SetPageTitle]: function({ commit }, title) {
      commit(AppMutations.SetPageTitle, title);
    },
    [AppActions.SetRefreshTokenModalIsVisible]: ({ commit }, isVisible) => {
      commit(AppMutations.UpdateRefreshTokenModalIsVisible, isVisible);
    },
    [AppActions.UpdateLastActiveTime]: function({ commit }) {
      commit(AppMutations.UpdateLastActiveTime);
    },
    [AppActions.ResetSessionInactiveTime]: function({ commit }) {
      commit(AppMutations.ResetSessionInactiveTime);
    },
    [AppActions.RetrieveSession]: function({ state, commit, dispatch }) {
      commit(AppMutations.SetLoading);
      if (!state.currentUser) {
        return appService
          .getSession()
          .then(user => {
            commit(AppMutations.RetrieveSessionSuccess, user);
          })
          .then(() => {
            dispatch(AppActions.RetrieveUnreadNotificationCount);
          })
          .then(() => {
            dispatch(AppActions.RetrieveGlobalValues);
          })
          .then(() => {
            dispatch(AppActions.RetrieveRecentMenuItems);
          })
          .then(() => {
            dispatch(AppActions.RetrieveRecentItemsList);
          })
          .then(() => {
            dispatch(TolErrorsActions.ClearAll);
          })
          .catch(error => {
            dispatch(TolErrorsActions.SetError, { error });
            commit(AppMutations.SetLoading, false);
          });
      }
    },
    [AppActions.SetRedirect]: ({ commit }, redirect) => {
      commit(AppMutations.SetRedirect, redirect);
    },
    [AppActions.RetrieveGlobalValues]: ({ commit }) => {
      return appService.getGlobalValues().then(value => {
        commit(AppMutations.RetrieveGlobalValuesSuccess, value);
      });
    },
    [AppActions.RetrieveUnreadNotificationCount]: ({ commit }) => {
      return appService.getUnreadNotificationCount().then(value => {
        commit(AppMutations.RetrieveUnreadNotificationCountSuccess, value);
      });
    },
    [AppActions.RefreshSession]: ({ dispatch }) => {
      appService.refreshSession().then(response => {
        localStorage.setItem('tol-token', response.data.accessToken);
        localStorage.setItem('tol-refresh-token', response.data.refreshToken);
        const tokenData = JSON.parse(
          Buffer.from(response.data.accessToken.split('.')[1], 'base64')
        );
        const FIVE_MINUTES_BEFORE_EXPIRY =
          (tokenData.exp - Math.floor(Date.now() / 1000) - 300) * 1000;
        dispatch(
          AppActions.InsertTimeOutReferences,
          setTimeout(() => {
            dispatch(AppActions.SetRefreshTokenModalIsVisible, true);
          }, FIVE_MINUTES_BEFORE_EXPIRY)
        );
        dispatch(AppActions.SetSessionRefreshTime, FIVE_MINUTES_BEFORE_EXPIRY);
        dispatch(AppActions.SetRefreshTokenModalIsVisible, false);
      });
    },
    [AppActions.AutoRefreshSession]: ({ state, dispatch }) => {
      if (Date.now() - state.lastUserActiveTime < 1800000) {
        dispatch(AppActions.RefreshSession);
      }
    },
    [AppActions.DismissSession]: function({ commit }) {
      appService.logoutSession().then(() => {
        localStorage.setItem('tol-token', null);
        localStorage.setItem('tol-refresh-token', null);
        commit(AppMutations.DismissSession);
      });
    },
    [AppActions.ToggleUserType]: function({ commit }) {
      commit(AppMutations.ToggleUserType);
    },
    [AppActions.RetrieveRecentMenuItems]: ({ state, dispatch }) => {
      dispatch(RecentItemsActions.RetrieveRecentItemsForMenu, state.currentUser.id);
    },
    [AppActions.RetrieveRecentItemsList]: ({ state, dispatch }) => {
      dispatch(RecentItemsActions.RetrieveRecentItemsList, state.currentUser.id);
    },
    [AppActions.InsertTimeOutReferences]: function({ commit }, reference) {
      commit(AppMutations.InsertTimeOutReferences, reference);
    },
    [AppActions.SetSessionRefreshTime]: ({ commit, dispatch }, refreshTime) => {
      commit(AppMutations.SetSessionRefreshTime, refreshTime);
      setTimeout(() => {
        dispatch(AppActions.AutoRefreshSession);
      }, refreshTime);
    },
    [AppActions.SetUserLocale]: ({ commit }, locale) => {
      commit(AppMutations.SetUserLocale, locale);
    },
    [AppActions.ResetAllModulesStates]: () => {
      /**
       * TODO
       * Clear all stores on logout and also when user is impersonated by admin
       * Jira #: TOL4-974
       */
    },
    [AppActions.SetAccountVerificationRequired]: ({ commit }, payload) => {
      commit(AppMutations.UpdateAccountVerificationRequired, payload);
    },
    [AppActions.CheckServer]: ({ commit }) => {
      return appService.getServiceAlive().then(res => {
        commit(AppMutations.SetServiceUnavailable, res.status);
      });
    }
  }
};
